import React, { useEffect } from 'react'
import HydrationSplitter from '../HydrationSplitter.jsx'
import Hero from '../organisms/Hero/Hero'
import ProfileIntro from '../organisms/ProfileIntro/ProfileIntro'
import DeprecatedSection from '../atoms/Section/Section'
import ContentPicture from '../molecules/ContentPicture/ContentPicture'
import News from '../organisms/News/News'
import LongDescription from '../molecules/LongDescription/LongDescription'
import Testimonials from '../organisms/Testimonials/Testimonials'
import TeamMembers from '../organisms/TeamMembers/TeamMembers'
import StickyBar from '../organisms/StickyBar/StickyBar'
import CenterReviews from '../organisms/CenterReviews/CenterReviews.jsx'
import TaxonomySections from '../organisms/TaxonomySections/TaxonomySections'
import VerifiedInfo from '../organisms/VerifiedInfo/VerifiedInfo'
import NavMarker from '../organisms/StickyBar/NavMarkers.jsx'
import InsuranceInformation from '../organisms/InsuranceInformation/InsuranceInformation'
import { ZoneIds } from '../../domain/zoneIds.ts'
import Container from '@/components/Container'
import Section from '@/components/Section'
import Map from '@/components/Map'
import { shouldDisplayVerificationOfBenefits } from '../../domain/useCases/contactBarDisplay.js'
import { shouldDisplayInsuranceBlock } from '../../domain/useCases/insurance/shouldDisplayInsuranceBlock'
import {
  computeAllReviewsCount,
  computeAllReviewsWeightedAverage
} from '../../domain/centerReviews/centerReviews.js'
import generatePremiumProfileNavigationLinks from '../../domain/center/useCases/generatePremiumProfileNavigationLinks'
import VirtualTour, {
  VIRTUAL_TOUR_ID
} from '../organisms/VirtualTour/VirtualTour'
import appendUtmSourceAndMediumToUrl from '../../domain/appendUtmSourceAndMediumToUrl.ts'
import { getWebsite } from '../../domain/center.js'
import { isNonEmptyArray } from '../../utils/checks.ts'
import CenterCarousel from '../../components/molecules/CenterCarousel/CenterCarousel'
import { Page } from '../../types/models/PageType.model'

const TOP_WINDOW = 0
const TOP_WINDOW_OFFSET = 100

const PremiumCenter = (props) => {
  const [showEmailNavbar, setShowEmailNavbar] = React.useState(false)

  const data = {
    ...props.data,
    website: appendUtmSourceAndMediumToUrl(getWebsite(props.data))
  }

  const nativeReviews = props.reviews.map((item) => ({
    ...item.node
  }))

  const allReviewsCount = computeAllReviewsCount(props.data)
  const allReviewsWeightedAverage = computeAllReviewsWeightedAverage(props.data)

  const doShowInsuranceInfo = shouldDisplayInsuranceBlock({
    acceptedInsurance: props.data?._insurance_info,
    insuranceDescription: props.data?.insurance_description,
    verifyInsuranceLink: props.data?.verify_insurance_link,
    phone: props.data?.phone
  })

  const navigationLinks = generatePremiumProfileNavigationLinks({
    data,
    showInsuranceInformation: doShowInsuranceInfo,
    showStaff: data?.team_members > 0
  })

  useEffect(() => {
    const handleScroll = () => {
      const centerName = document.getElementById('profile-intro-center-name')
      if (!centerName) {
        return
      }
      const centerNameRect = centerName.getBoundingClientRect()

      if (!showEmailNavbar && centerNameRect.y < TOP_WINDOW) {
        setShowEmailNavbar(true)
      }
      // this is to prevent an issue with iOS where the content shift when seting something sticky and Y can change,
      if (showEmailNavbar && centerNameRect.y > TOP_WINDOW_OFFSET) {
        setShowEmailNavbar(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  })

  return (
    <div>
      {data.header_image && (
        <Hero
          size="medium"
          noOverlay
          background={data.header_image}
        />
      )}
      {data.claimed_email && (
        <StickyBar
          data={data}
          queryID={props.queryID}
          showEmailNavbar={showEmailNavbar}
          navigationLinks={navigationLinks}
          reviewsCount={allReviewsCount}
          reviewsAverage={allReviewsWeightedAverage}
        />
      )}

      <HydrationSplitter>
        <ProfileIntro
          queryID={props.queryID}
          breadcrumbs={props.breadcrumbs}
          allReviewsCount={allReviewsCount}
          allReviewsWeightedAverage={allReviewsWeightedAverage}
          data={data}
          virtualTourElementId={VIRTUAL_TOUR_ID}
          pageType={Page.CENTER_PROFILE}
          centerId={props.data.id}
        />
      </HydrationSplitter>

      <HydrationSplitter>
        <DeprecatedSection
          id="mobileTarget"
          padding="40px"
        >
          <NavMarker id="profile-about">
            <LongDescription
              centerTitle={data.title}
              verify_insurance_link={props.data.verify_insurance_link}
              centerSlug={data.slug}
              video_embed={data.video_embed}
              dangerouslySetInnerHTML={{
                __html: `<p>${data.long_description}</p>`
              }}
            />
          </NavMarker>
        </DeprecatedSection>
      </HydrationSplitter>

      <HydrationSplitter>
        {doShowInsuranceInfo && (
          <NavMarker id="profile-insurance">
            <InsuranceInformation
              center={data}
              insurance_description={data.insurance_description}
              showInfoLinks
              verify_insurance_link={data.verify_insurance_link}
              shouldDisplayInsuranceBlock={shouldDisplayInsuranceBlock({
                acceptedInsurance: data?._insurance_info,
                insuranceDescription: data?.insurance_description,
                verifyInsuranceLink: data?.verify_insurance_link,
                phone: data?.phone
              })}
              shouldShowVerifyBenefits={shouldDisplayVerificationOfBenefits(
                data?.benefitsProvider
              )}
              benefitsKey={data?.benefitsKey}
              benefitsProvider={data?.benefitsProvider}
              centerTitle={data?.title}
              phoneNumber={data?.phone}
            />
          </NavMarker>
        )}
      </HydrationSplitter>

      <CenterCarousel
        centers={props.ourCenters.map((item) => item.node).filter(Boolean)}
        sectionHeading={props.data.our_centers_header || 'Our Centers'}
        headerTag="h4"
        headerAlignment="center"
        headerSize="2xl"
        className="py-5"
        containerId="ourCenters"
        zoneId="Brand"
        isLoading={false}
        shouldHideAdTags={true}
      />

      <HydrationSplitter>
        <NavMarker id="profile-treatment">
          {data.text_picture !== null && (
            <React.Fragment>
              {data.text_picture.map((item, i) => {
                if (i % 2 === 0) {
                  return (
                    <div
                      className="mb-6 md:mb-12"
                      key={item.title}
                    >
                      <Section>
                        <Container>
                          <ContentPicture
                            zoneId={ZoneIds.Featured}
                            title={item.title}
                            content={item.content}
                            link={
                              item.link
                                ? { link: item.link, text: 'Learn More' }
                                : undefined
                            }
                            image={`https://res.cloudinary.com/rehabpath/image/upload/${item.image
                              ?.split('/')
                              ?.pop()}`}
                            charcoal
                          />
                        </Container>
                      </Section>
                    </div>
                  )
                } else {
                  return (
                    <div
                      className="mb-6 md:mb-12"
                      key={item.title}
                    >
                      <Section>
                        <Container>
                          <ContentPicture
                            gray={true}
                            reverse
                            title={item.title}
                            content={item.content}
                            link={
                              item.link
                                ? { link: item.link, text: 'Learn More' }
                                : undefined
                            }
                            image={`https://res.cloudinary.com/rehabpath/image/upload/${item.image
                              ?.split('/')
                              ?.pop()}`}
                          />
                        </Container>
                      </Section>
                    </div>
                  )
                }
              })}
            </React.Fragment>
          )}
        </NavMarker>
      </HydrationSplitter>

      {data.certifications && (
        <HydrationSplitter>
          <VerifiedInfo
            title={data.title}
            certs={data.certifications}
            description={props.data.verified_description}
          />
        </HydrationSplitter>
      )}

      {data && (
        <HydrationSplitter>
          <TaxonomySections
            data={{
              _treatment_method: data?._treatment_method || [],
              treatment_method: data?.treatment_method || [],
              overviewAddress: data.address,
              founded: data.founded_date,
              occupancy: data.occupancy,
              specializations: data.specializations,
              onsite_activities: data.onsite_activities,
              off_site_activities: data.off_site_activities,
              treatment_services: data.treatment_services,
              languages: data.languages,
              price_length: data.price_length,
              therapies: data.treatment_therapies,
              _therapies: data._treatment_therapies,
              aftercare: data.aftercare,
              onsite_amenities: data.onsite_amenities,
              off_site_amenities: data.off_amenities,
              personal_amenities: data.personal_amenities,
              price_label: data.price_label,
              length_label: data.length_label,
              amenities: [
                ...(data.onsite_amenities || []),
                ...(data.off_amenities || []),
                ...(data.personal_amenities || [])
              ],
              accredidation: data.accredidation,
              special_considerations: data.special_considerations,
              activities: [
                ...(data.onsite_activities || []),
                ...(data.off_site_activities || [])
              ],
              who_we_treat: data._who_we_treat,
              new_specializations: data._new_specializations,
              _off_site_activities: data._off_site_activities,
              _amenities: data._amenities,
              _onsite_activities: data._onsite_activities,
              _special_considerations: data._special_considerations,
              _what_we_treat: data._what_we_treat,
              treatment_therapies: data._treatment_therapies,
              arranged_aftercare: data._arranged_aftercare,
              included_aftercare: data._included_aftercare,
              what_we_treat: data.what_we_treat,
              _treatment_services: data._treatment_services,
              is_standard: false,
              taxonomyList: props.taxonomyList,
              primaryFocus: data.primaryFocus,
              acceptsInsurance: data?.accepted_insurance?.length > 0
            }}
          />
        </HydrationSplitter>
      )}

      {isNonEmptyArray(data.team_members) && (
        <NavMarker id="profile-staff">
          <Section>
            <div className="border-t-1 mt-6 h-6 border-gray-200 md:mt-12 md:h-12" />
            <Container>
              <TeamMembers data={data.team_members} />
            </Container>
          </Section>
        </NavMarker>
      )}

      <HydrationSplitter>
        {isNonEmptyArray(data.news) && (
          <DeprecatedSection
            fluid
            gray
            style={{ maxWidth: '1212px', margin: 'auto' }}
          >
            <NavMarker
              style={{
                paddingTop: '64px'
              }}
              id="profile-news"
            >
              <News
                title={data.title}
                data={data.news}
              />
            </NavMarker>
          </DeprecatedSection>
        )}
      </HydrationSplitter>

      <HydrationSplitter>
        <DeprecatedSection
          fluid
          style={{ maxWidth: '1212px', margin: 'auto' }}
        >
          {data.testimonials && data.testimonials.length > 0 && (
            <Testimonials
              background={data.testimonial_background}
              data={[data.testimonials[0]]}
            />
          )}
        </DeprecatedSection>
      </HydrationSplitter>

      <HydrationSplitter>
        <NavMarker
          spacing="60px"
          id="profile-reviews"
        >
          <DeprecatedSection className="sub-nav-item"></DeprecatedSection>
        </NavMarker>
      </HydrationSplitter>

      <HydrationSplitter>
        <CenterReviews
          center={data}
          nativeReviews={nativeReviews}
        />
      </HydrationSplitter>

      <HydrationSplitter>
        {props.data?.virtual_tour_embed && (
          <VirtualTour
            embedSrc={props.data.virtual_tour_embed}
            containerClass="relative mx-auto mb-6 w-full max-w-[1600px]"
            iframeClass="w-full h-[453px] md:h-[400px]"
          />
        )}
      </HydrationSplitter>

      <HydrationSplitter>
        <Section>
          <Container size="lg">
            <Map
              query={data.location_query}
              locationLabel={data.address || data.location_label}
              title={data.title}
            />
          </Container>
        </Section>
      </HydrationSplitter>
    </div>
  )
}

export default PremiumCenter
